import { Input, Modal, Table } from "antd";
import { useCallback, useState, useMemo, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import { Doughnut } from 'react-chartjs-2';
import { AppState } from "store";
import { initializeIssues, onChangeIssuesFilters, setIssuessSearch } from "store/actions";
import { parseIssuesByCategory, parseIssuesTable } from "utils/parsers";
import { searchIcon } from "svgs";

ChartJS.register(ArcElement, Tooltip, Legend);

// ─── Issue Colors ───
const ISSUE_COLORS: { [key: string]: { background: string; border: string } } = {
  'Accident': {
    background: 'rgba(255, 165, 0, 1)',
      border: 'rgba(200, 200, 200, 0.15)',
  },
  'ACCIDENT': {
    background: 'rgba(255, 165, 0, 1)',
      border: 'rgba(200, 200, 200, 0.15)',
  },
  'Account Related': {
    background: 'rgba(255, 215, 0, 1)',
      border: 'rgba(200, 200, 200, 0.15)',
  },
  'ACCOUNT_RELATED': {
    background: 'rgba(255, 215, 0, 1)',
      border: 'rgba(200, 200, 200, 0.15)',
  },
  'Account related': {
    background: 'rgba(255, 215, 0, 1)',
       border: 'rgba(200, 200, 200, 0.15)',
  },
  'Breakdown': {
    background: 'rgba(128, 0, 0, 1)',
       border: 'rgba(200, 200, 200, 0.15)',
  },
  'BREAKDOWN': {
    background: 'rgba(128, 0, 0, 1)',
       border: 'rgba(200, 200, 200, 0.15)',
  },
  'Roadside Breakdown': {
    background: 'rgba(128, 0, 0, 1)',
       border: 'rgba(200, 200, 200, 0.15)',
  },
  'ROADSIDE_BREAKDOWN': {
    background: 'rgba(128, 0, 0, 1)',
      border: 'rgba(200, 200, 200, 0.15)',
  },
  'Unknown': {
    background: 'rgba(190, 72, 190, 1)',
       border: 'rgba(200, 200, 200, 0.15)',
  },
  'UNKNOWN': {
    background: 'rgba(192, 71, 192, 1)',
       border: 'rgba(200, 200, 200, 0.15)',
  },
  'Mechanical failure': {
    background: 'rgba(128, 0, 128, 1)',
       border: 'rgba(200, 200, 200, 0.15)',
  },
  'MECHANICAL_FAILURE': {
    background: 'rgba(128, 0, 128, 1)',
      border: 'rgba(200, 200, 200, 0.15)',
  },
  'Vehicle Missing': {
    background: 'rgba(220, 20, 60, 1)',
       border: 'rgba(200, 200, 200, 0.15)',
  },
  'VEHICLE_MISSING': {
    background: 'rgba(220, 20, 60, 1)',
       border: 'rgba(200, 200, 200, 0.15)',
  },
};

const DEFAULT_ISSUE_COLOR = {
  background: 'rgba(128, 128, 128, 1)',
  border: 'rgba(128, 128, 128, 0.3)',
};

const getIssueColor = (issueType: string) => {
  if (ISSUE_COLORS[issueType]) {
    return ISSUE_COLORS[issueType];
  }

  const normalizedType = issueType?.toLowerCase().trim();

  if (normalizedType?.includes('accident')) return ISSUE_COLORS['Accident'];
  if (normalizedType?.includes('account')) return ISSUE_COLORS['Account Related'];
  if (normalizedType?.includes('breakdown') || normalizedType?.includes('roadside'))
    return ISSUE_COLORS['Breakdown'];
  if (normalizedType?.includes('unknown') || normalizedType?.includes('mechanical'))
    return ISSUE_COLORS['Unknown'];
  if (normalizedType?.includes('missing') || normalizedType?.includes('vehicle missing'))
    return ISSUE_COLORS['Vehicle Missing'];

  return DEFAULT_ISSUE_COLOR;
};

export default function IssueRenderChart() {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [shouldOpenModal, setShouldOpenModal] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const chartRef = useRef(null);

  const { userType } = useSelector<AppState, AppState["common"]>(
    (state) => state.common
  );
  const dispatch = useDispatch();
  const {
    issues,
    page,
    search,
    issuesCountWithType,
    filters: { updated, listType },
  } = useSelector<AppState, AppState["issues"]>((state) => state.issues);

  const data = useMemo(
    () => parseIssuesTable(search, issues, listType),
    [issues, listType, search]
  );

  const categoryData = useMemo(
    () => parseIssuesByCategory(issuesCountWithType),
    [issuesCountWithType]
  );

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const isExtraSmall = windowWidth < 300;
  const isMobile = windowWidth < 768;
  const isTablet = windowWidth >= 768 && windowWidth < 1024;

  // ─── Total issues count ───
  const totalIssues = categoryData.reduce(
    (acc: number, curr: any) => acc + Number(curr.total_issues),
    0
  );

  const filteredTableData = useMemo(() => {
    if (!selectedCategory) return data;

    const filtered = data.filter((issue: any) => {
      const issueType = issue.issue_type;

      if (selectedCategory === "Roadside Breakdown" || selectedCategory === "Breakdown") {
        return (
          issueType === "ROADSIDE_BREAKDOWN" ||
          issueType === "Roadside Breakdown" ||
          issueType === "BREAKDOWN" ||
          issueType === "Breakdown"
        );
      } else if (selectedCategory === "Vehicle Missing") {
        return issueType === "VEHICLE_MISSING" || issueType === "Vehicle Missing";
      } else if (selectedCategory === "Unknown") {
        return (
          issueType === "Mechanical failure" ||
          issueType === "MECHANICAL_FAILURE" ||
          issueType === "Unknown" ||
          issueType === "UNKNOWN" ||
          !issueType ||
          issueType === ""
        );
      } else if (selectedCategory === "Accident") {
        return issueType === "ACCIDENT" || issueType === "Accident";
      } else if (
        selectedCategory === "Account Related" ||
        selectedCategory === "Account related"
      ) {
        return (
          issueType === "ACCOUNT_RELATED" ||
          issueType === "Account Related" ||
          issueType === "Account related"
        );
      } else {
        return (
          issueType === selectedCategory ||
          issueType?.toLowerCase() === selectedCategory.toLowerCase() ||
          issueType?.toUpperCase() === selectedCategory.toUpperCase() ||
          issueType?.toUpperCase().replace(/\s+/g, "_") ===
            selectedCategory.toUpperCase().replace(/\s+/g, "_")
        );
      }
    });

    return filtered;
  }, [data, selectedCategory]);

  const pieChartData = useMemo(() => {
    const issueTypes = categoryData.map((issue: any) => issue.issue_type);
    const backgroundColors = issueTypes.map(
      (type: string) => getIssueColor(type).background
    );
    const borderColors = issueTypes.map(
      (type: string) => getIssueColor(type).border
    );

    return {
      labels: issueTypes,
      datasets: [
        {
          data: categoryData.map((issue: any) => Number(issue.total_issues)),
          backgroundColor: backgroundColors,
          borderColor: borderColors,
          borderWidth: 2,
          hoverOffset: isMobile ? 5 : 10,
        },
      ],
    };
  }, [categoryData, isMobile]);

  // ─── Chart options — legend HIDDEN ───
  const chartOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      cutout: "55%",
      layout: {
        padding: isExtraSmall ? 1 : isMobile ? 3 : 5,
      },
      plugins: {
        legend: {
          display: false, // ✅ Legend hidden
        },
        tooltip: {
          callbacks: {
            label: function (context: any) {
              const label = context.label || "";
              const value = context.parsed || 0;
              const total = context.dataset.data.reduce(
                (a: number, b: number) => a + b,
                0
              );
              const percentage = ((value / total) * 100).toFixed(1);
              return `${label}: ${value} issues (${percentage}%)`;
            },
          },
          bodyFont: {
            size: isExtraSmall ? 9 : isMobile ? 10 : 12,
          },
        },
      },
      onClick: (event: any, elements: any) => {
        if (userType !== 2 && elements.length > 0) {
          const elementIndex = elements[0].index;
          const category = categoryData[elementIndex].issue_type;
          handlePieClick(category);
        }
      },
    }),
    [isExtraSmall, isMobile, userType, categoryData]
  );

  const onChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;
      setSearchInput(value);
      dispatch(setIssuessSearch(value));
    },
    [dispatch]
  );

  const handlePieClick = (category: string) => {
    setSelectedCategory(category);
    setShouldOpenModal(true);

    if (category === "Roadside Breakdown" || category === "Breakdown") {
      dispatch(onChangeIssuesFilters("Issue Type", ["ROADSIDE_BREAKDOWN"], "list"));
    } else if (category === "Vehicle Missing") {
      dispatch(onChangeIssuesFilters("Issue Type", ["VEHICLE_MISSING"], "list"));
    } else if (category === "Account Related" || category === "Account related") {
      dispatch(onChangeIssuesFilters("Issue Type", ["ACCOUNT_RELATED"], "list"));
    } else if (category === "Unknown" || category === "Mechanical failure") {
      dispatch(onChangeIssuesFilters("Issue Type", ["UNKNOWN"], "list"));
    } else if (category === "Accident") {
      dispatch(onChangeIssuesFilters("Issue Type", ["ACCIDENT"], "list"));
    } else {
      dispatch(
        onChangeIssuesFilters(
          "Issue Type",
          [category.toUpperCase().replace(/\s+/g, "_")],
          "list"
        )
      );
    }
  };

  // ─── Stats card click → same as pie click ───
  const handleStatClick = (issueType: string) => {
    if (userType !== 2) {
      handlePieClick(issueType);
    }
  };

  useEffect(() => {
    if (updated && shouldOpenModal) {
      dispatch(initializeIssues(1, updated, listType));
      setIsModalVisible(true);
      setShouldOpenModal(false);
    }
  }, [updated, shouldOpenModal, dispatch, listType]);

  const filteredCategoryData = useMemo(() => {
    if (!selectedCategory) return [];
    return categoryData.filter(
      (issue: any) => issue.issue_type === selectedCategory
    );
  }, [selectedCategory, categoryData]);

  const columns = [
    {
      title: "Issue ID",
      dataIndex: "issue_id",
      width: isMobile ? 80 : 100,
      ellipsis: true,
      fixed: isMobile ? ("left" as const) : undefined,
    },
    {
      title: "Vehicle ID",
      dataIndex: "vehicle_id",
      width: isMobile ? 80 : 100,
      ellipsis: true,
    },
    {
      title: "Issue Type",
      dataIndex: "issue_type",
      width: isMobile ? 100 : 130,
      ellipsis: true,
      align: "center" as const,
    },
    {
      title: "Due Date",
      dataIndex: "due_date",
      width: isMobile ? 90 : 120,
      ellipsis: true,
      align: "center" as const,
      responsive: ["sm"] as any,
    },
  ];

  const getChartHeight = () => {
    if (isExtraSmall) return "200px";
    if (isMobile) return "250px";
    if (isTablet) return "280px";
    return "150px";
  };

  const getModalWidth = () => {
    if (isMobile) return "95%";
    if (isTablet) return "90%";
    return 800;
  };

  return (
    <div className="issues-chart-container">
      {/* ✅ Header with total badge */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: "5px",
        }}
      >
        <h3 style={{ margin: 0, fontSize: isMobile ? "14px" : "16px" }}>
          Summary of Issues
        </h3>
        {totalIssues > 0 && (
          <span
            style={{
              fontSize: isMobile ? 11 : 13,
              color: "#888",
              background: "#f5f5f5",
              padding: "2px 8px",
              borderRadius: "10px",
            }}
          >
            Total: {totalIssues}
          </span>
        )}
      </div>

      {/* Chart */}
      <div className="pie-chart-wrapper">
        {categoryData.length > 0 ? (
          <div
            className="pie-chart-container"
            style={{
              height: getChartHeight(),
              width: "100%",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              position: "relative",
              padding: isMobile ? "10px" : "0",
            }}
          >
            <Doughnut
              ref={chartRef}
              data={pieChartData}
              options={chartOptions}
            />
          </div>
        ) : (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "150px",
              textAlign: "center",
            }}
          >
            <p>No records available to display.</p>
          </div>
        )}
      </div>

      {/* ✅ Stats Cards — replaces legend */}
   {categoryData.length > 0 && (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: isMobile
              ? "repeat(2, minmax(0, 1fr))"
              : `repeat(${Math.min(categoryData.length, 5)}, minmax(0, 1fr))`,
            gap: isMobile ? "6px" : "8px",
            marginTop: "15px",
            width: "100%",
            maxWidth: "100%",
            overflow: "hidden",
            boxSizing: "border-box",
          }}
        >
          {categoryData.map((item: any) => {
            const color = getIssueColor(item.issue_type);
            return (
              <div
                key={item.issue_type}
                onClick={() => handleStatClick(item.issue_type)}
                style={{
                  textAlign: "center",
                  padding: isMobile ? "6px 4px" : "8px 4px",
                  borderRadius: "8px",
                  background: color.border,
                  cursor: userType !== 2 ? "pointer" : "default",
                  transition: "transform 0.2s, box-shadow 0.2s",
                  border: `1px solid ${color.background}20`,
                  minWidth: 0,              // ✅ Prevents grid blowout
                  overflow: "hidden",       // ✅ Contains content
                  boxSizing: "border-box",  // ✅ Includes padding in width
                }}
                onMouseEnter={(e) => {
                  if (userType !== 2) {
                    e.currentTarget.style.transform = "scale(1.05)";
                    e.currentTarget.style.boxShadow = `0 2px 8px ${color.background}40`;
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "scale(1)";
                  e.currentTarget.style.boxShadow = "none";
                }}
              >
                <div
                  style={{
                    fontSize: isMobile ? "16px" : "20px",
                    fontWeight: 700,
                    color: color.background,
                  }}
                >
                  {item.total_issues}
                </div>
                <div
                  style={{
                    fontSize: isMobile ? "9px" : "11px",
                    color: "#555",
                    marginTop: "2px",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    maxWidth: "100%",        // ✅ Text stays inside
                  }}
                  title={item.issue_type}    // ✅ Full text on hover
                >
                  {item.issue_type}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Modal */}
      <Modal
        visible={isModalVisible}
        onCancel={() => {
          setIsModalVisible(false);
          setSelectedCategory(null);
          setShouldOpenModal(false);
          dispatch(setIssuessSearch(""));
          setSearchInput("");
          dispatch(onChangeIssuesFilters("Issue Type", [], "list"));
        }}
        footer={null}
        width={getModalWidth()}
        className="issues-modal"
        bodyStyle={{
          padding: isMobile ? "12px" : "24px",
          maxHeight: isMobile ? "70vh" : "80vh",
          overflow: "auto",
        }}
      >
        <Input
          className="--standard issues-search-input"
          placeholder="Search Issues..."
          prefix={searchIcon}
          value={searchInput}
          onChange={onChange}
          style={{ marginBottom: isMobile ? "12px" : "16px" }}
        />
        <div className="issues-table-container">
          {filteredTableData.length > 0 ? (
            <Table
              dataSource={filteredTableData}
              columns={columns}
              pagination={isMobile ? { pageSize: 5, size: "small" } : false}
              scroll={{
                y: isMobile ? 250 : 300,
                x: isMobile ? "max-content" : undefined,
              }}
              size={isMobile ? "small" : "middle"}
            />
          ) : (
            <p>No data available for this category.</p>
          )}
        </div>
      </Modal>
    </div>
  );
}